import {
    CostCenterFilter,
    EmployeeFilter,
    ExpenseKindEnum,
    ExpenseStatusEnum,
    SubscriptionTypeEnum,
    TransactionCategoryTypeEnum,
    TransactionTypeEnum,
    VendorFilter,
    WorkflowFilter,
} from "@finway-group/shared/lib/models"
import { TransactionStatusEnum } from "@finway-group/shared/lib/models/transaction/transactionStatus.enum"
import { SortOrder } from "antd/lib/table/interface"

import { getCostCentersFromStore } from "Shared/hooks/costCenter.hooks"
import { getEmployeesFromStore } from "Shared/hooks/employee.hooks"
import { getTableFilterObjectStore, tableOptions } from "Shared/hooks/table.hooks"
import { getParentTransactionCategoriesFromStore } from "Shared/hooks/transactionCategory.hooks"
import { getVendorsFromStore } from "Shared/hooks/vendor.hooks"
import { getWorkflowsFromStore } from "Shared/hooks/workflow.hooks"
import { AnalyticsService, ExpenseService, UserService } from "Shared/services"
import CardService from "Shared/services/card.service"
import InboxInvoiceService from "Shared/services/inboxInvoice.service"
import TransactionService from "Shared/services/transaction.service"
import store from "Shared/store"
import { fetchTableSuccess } from "Shared/store/actions/tables/tableActions"
import { TableActionsTypes, UpdateTableFilterType } from "Shared/store/actions/tables/tableTypes.enum"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { CostCenterWithBudgetData, EmployeeWithExpenseData, Table, TransactionCategoryWithChildren, VendorWithExpenseData } from "Shared/store/reducers/tableReducer"

import { getFilteredCostCenter1, getFilteredEmployees, getFilteredVendors, getFilteredWorkflows, getFilterQueryString } from "./filter.utils"
import { convertSearchValueToQuery } from "./helpers"

export enum TableDocType {
    INBOX_INVOICE = "INBOX_INVOICE",
    EXPENSE = "EXPENSE",
    TRANSACTION = "TRANSACTION",
    SUBSCRIPTION = "SUBSCRIPTION",
    CARD = "CARD",
    EMPLOYEE = "EMPLOYEE",
    COST_CENTER = "COST_CENTER",
    VENDOR = "VENDOR",
    WORKFLOW = "WORKFLOW",
    TRANSACTION_CATEGORY = "TRANSACTION_CATEGORY",
}

export const getTableActionType = (TableActionsTypes: TableActionsTypes, tablesEnum: TablesEnum): string => `${TableActionsTypes}_${tablesEnum}`

export const getSortOrderForColumn = (table: Table<any>, fieldName: string) => {
    return table.data.sort.field === fieldName ? (table.data.sort.order as SortOrder) : undefined
}

export const fetchTableData = async (table: TablesEnum, queryParams: any, source: any, options: tableOptions, timeout?: number) => {
    const tableDocType = getTableDocType(table)

    switch (tableDocType) {
        case TableDocType.INBOX_INVOICE:
            if (table === TablesEnum.INBOX_INVOICE_MODAL_INVOICES && options.expenseId) {
                queryParams.filterQueryString = `${queryParams.filterQueryString}&expense[eq]=${options.expenseId}`
            }
            return InboxInvoiceService.fetchInboxInvoices(queryParams, source, timeout)
        case TableDocType.EXPENSE:
            if (table === TablesEnum.INBOX_INVOICE_MODAL_EXPENSES && options.inboxInvoiceId) {
                queryParams.filterQueryString = `${queryParams.filterQueryString}&inboxInvoice[eq]=${options.inboxInvoiceId}`
            } else if (table === TablesEnum.TODO_DOCS_NEEDED) {
                queryParams = {
                    ...queryParams,
                    // special case handling for quick filter presets
                    filterQueryString: `${queryParams.filterQueryString}&docsNeeded[eq]=true`,
                }
            }
            return ExpenseService.fetchAllExpenses(queryParams, source, timeout)
        case TableDocType.CARD:
            return CardService.fetchAllCards(queryParams, source, timeout)
        case TableDocType.TRANSACTION:
            if (table === TablesEnum.CARD_TRANSACTIONS && options.cardId) {
                return TransactionService.fetchCardTransactions(queryParams, source, options.cardId, timeout)
            } else if (table === TablesEnum.EXPENSE_TRANSACTIONS && options.expenseId) {
                queryParams.filterQueryString = `${queryParams.filterQueryString}&expense[eq]=${options.expenseId}`
            }

            return TransactionService.fetchAllTransactions(queryParams, source, timeout)

        /*
            The employee table is not from the api re-fetched so we have to filter on the frontend
        */
        case TableDocType.EMPLOYEE:
            const loggedInUser = UserService.getLoggedInEmployeeProfile()
            const employees = getEmployeesFromStore(true, false, false, queryParams.search)
            const employeesCopy = JSON.parse(JSON.stringify(employees))
            const tableFilterObject = getTableFilterObjectStore<EmployeeFilter>(table)

            // if user is admin add expense data
            if (loggedInUser.activeCompanyProfile.isAdmin) {
                const groupBy = table === TablesEnum.EMPLOYEE_VENDORS ? "vendor" : "requestedBy"
                const employeeIds = employees.map((employee) => employee.id)
                AnalyticsService.fetchExpenseDataForGroup(groupBy, employeeIds, loggedInUser.settings.globalCurrency)
                    .then((expenseData) => {
                        for (const employee of employeesCopy as EmployeeWithExpenseData[]) {
                            employee.expenseData = expenseData.find((doc: any) => doc.id === employee.id)
                        }
                        const filteredEmployees = getFilteredEmployees(tableFilterObject, employeesCopy as EmployeeWithExpenseData[])
                        store.dispatch(fetchTableSuccess(table, { docs: filteredEmployees, totalDocs: filteredEmployees.length }))
                    })
                    .catch(() => {}) // if expenseData fails to fetch, we catch the error to still show the employees table without expense data.
            }

            const filteredEmployees = getFilteredEmployees(tableFilterObject, employees as EmployeeWithExpenseData[])
            return { docs: filteredEmployees, totalDocs: filteredEmployees.length }

        case TableDocType.COST_CENTER: {
            const loggedInUser = UserService.getLoggedInEmployeeProfile()
            const costCenters = JSON.parse(JSON.stringify(getCostCentersFromStore(true, true, queryParams.search)))
            const costCentersCopy = JSON.parse(JSON.stringify(costCenters))
            const tableFilterObject = getTableFilterObjectStore<CostCenterFilter>(table)

            // add budget data to cost centers◊
            const costCenterIds = costCenters.map((costCenter: any) => costCenter._id)
            console.log(`Fetching CC expense data`)
            AnalyticsService.fetchCostCenterBudgetData(costCenterIds, loggedInUser.settings.globalCurrency)
                .then((budgetData) => {
                    console.log(`Fetched CC expense data`)
                    for (const costCenter of costCentersCopy as CostCenterWithBudgetData[]) {
                        costCenter.budgetData = budgetData.find((doc: any) => doc.id === costCenter._id)
                    }
                    const filteredCostCenters = getFilteredCostCenter1(tableFilterObject, costCentersCopy)
                    store.dispatch(fetchTableSuccess(table, { docs: filteredCostCenters, totalDocs: filteredCostCenters.length }))
                })
                // if budgetData fails to fetch, we catch the error to still show the cost center without budget data.
                .catch(() => {})

            const filteredCostCenters = getFilteredCostCenter1(tableFilterObject, costCenters)
            return { docs: filteredCostCenters, totalDocs: filteredCostCenters.length }
        }

        case TableDocType.VENDOR: {
            const loggedInUser = UserService.getLoggedInEmployeeProfile()
            const vendors = getVendorsFromStore(true, queryParams.search)
            const vendorsCopy = JSON.parse(JSON.stringify(vendors))
            const tableFilterObject = getTableFilterObjectStore<VendorFilter>(table)

            const vendorIds = vendors.map((vendor: any) => vendor.id)
            console.log(`Fetching vendor expense data`)
            AnalyticsService.fetchExpenseDataForGroup("vendor", vendorIds, loggedInUser.settings.globalCurrency)
                .then((expenseData) => {
                    console.log(`Fetched vendor expense data`)
                    for (const vendor of vendorsCopy as VendorWithExpenseData[]) {
                        vendor.expenseData = expenseData.find((doc: any) => doc.id === vendor.id)
                    }
                    const filteredVendors = getFilteredVendors(tableFilterObject, vendorsCopy)
                    store.dispatch(fetchTableSuccess(table, { docs: filteredVendors, totalDocs: filteredVendors.length }))
                })
                // if expenseData fails to fetch, we catch the error to still show the vendors without expense data.
                .catch(() => {})

            const filteredVendors = getFilteredVendors(tableFilterObject, vendors)
            return { docs: filteredVendors, totalDocs: filteredVendors.length }
        }

        case TableDocType.WORKFLOW: {
            const workflows = getWorkflowsFromStore(queryParams.search)
            const tableFilterObject = getTableFilterObjectStore<WorkflowFilter>(table)
            const filteredWorkflows = getFilteredWorkflows(tableFilterObject, workflows)

            return { docs: filteredWorkflows, totalDocs: filteredWorkflows.length }
        }

        case TableDocType.TRANSACTION_CATEGORY: {
            let transactionCategoryType: any
            if (table === TablesEnum.TRANSACTION_CATEGORIES_INFLOW) {
                transactionCategoryType = TransactionCategoryTypeEnum.INFLOW
            } else if (table === TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW) {
                transactionCategoryType = TransactionCategoryTypeEnum.OUTFLOW
            }

            const transactionCategoriesWithChildren: TransactionCategoryWithChildren[] = getParentTransactionCategoriesFromStore(transactionCategoryType, queryParams.search)

            return { docs: transactionCategoriesWithChildren, totalDocs: transactionCategoriesWithChildren.length }
        }

        default:
            break
    }
    return
}

/**
 * Returns the base filter query string the table always has, regardless of the filters the user sets.
 * NOTE: Do not add sorting here, it won't work because we dont currently support two sorts at the same time.
 */
export const getTableBaseFilterQueryString = (table: TablesEnum) => {
    const loggedInUserId = store.getState().auth.user?.id
    const archiveAfterXDaysQueryString = `&archiveAfterXDays[eq]=${store.getState().company.item.archiveAfterXDays}`
    const transactionArchiveAfterXDaysQueryString = `&archiveAfterXDays[eq]=${store.getState().company.item.transactionArchiveAfterXDays}`
    const notDeletedQueryString = "&deleted[is]=false"

    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
            return `&status[in]=${ExpenseStatusEnum.PURCHASE_PENDING}` + `&requestedBy[ne]=${loggedInUserId}` + notDeletedQueryString + archiveAfterXDaysQueryString
        case TablesEnum.TODO_INVOICE_APPROVAL:
            return `&status[in]=${ExpenseStatusEnum.INVOICE_PENDING}` + `&requestedBy[ne]=${loggedInUserId}` + notDeletedQueryString + archiveAfterXDaysQueryString
        case TablesEnum.TODO_INVOICE_AND_PURCHASE:
            return (
                `&status[in]=${ExpenseStatusEnum.INVOICE_PENDING},${ExpenseStatusEnum.PURCHASE_PENDING}` +
                `&requestedBy[ne]=${loggedInUserId}` +
                notDeletedQueryString +
                archiveAfterXDaysQueryString
            )
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
        case TablesEnum.TODO_DOCS_NEEDED:
            return `&status[in]=${ExpenseStatusEnum.DOCS_NEEDED}` + notDeletedQueryString + archiveAfterXDaysQueryString
        case TablesEnum.TODO_APPROVAL_PENDING:
            return (
                `&status[in]=${ExpenseStatusEnum.PURCHASE_PENDING},${ExpenseStatusEnum.INVOICE_PENDING}` +
                `&requestedBy[eq]=${loggedInUserId}` +
                notDeletedQueryString +
                archiveAfterXDaysQueryString
            )

        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
            return `&status[in]=${ExpenseStatusEnum.REVIEWED}` + notDeletedQueryString + archiveAfterXDaysQueryString
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
            return `&status[in]=${ExpenseStatusEnum.PAID}` + notDeletedQueryString + archiveAfterXDaysQueryString

        case TablesEnum.REVIEW_REQUESTS:
            return `&status[eq]=${ExpenseStatusEnum.APPROVED}` + notDeletedQueryString + archiveAfterXDaysQueryString

        case TablesEnum.DONE_REQUESTS:
            // to not show duplicates with initial recurring expense
            return (
                `&status[in]=${ExpenseStatusEnum.DONE},${ExpenseStatusEnum.DECLINED}&isFullyProcessedSubscription[is]=false` + notDeletedQueryString + archiveAfterXDaysQueryString
            )
        case TablesEnum.IN_PROGRESS:
            return (
                `&status[in]=${ExpenseStatusEnum.APPROVED},${ExpenseStatusEnum.REVIEWED},${ExpenseStatusEnum.PAID}&isFullyProcessedSubscription[is]=false` +
                notDeletedQueryString +
                archiveAfterXDaysQueryString
            )
        case TablesEnum.ALL_REQUESTS:
            return `&isFullyProcessedSubscription[is]=false` + notDeletedQueryString + archiveAfterXDaysQueryString
        case TablesEnum.ARCHIVE:
            return `&archived[is]=true&isFullyProcessedSubscription[is]=false` + notDeletedQueryString + archiveAfterXDaysQueryString

        case TablesEnum.SUBSCRIPTIONS:
            return (
                `&kind[eq]=${ExpenseKindEnum.SUBSCRIPTION}&subscriptionType[eq]=${SubscriptionTypeEnum.INITIAL_REQUEST}&status[in]=${ExpenseStatusEnum.PAID},${ExpenseStatusEnum.DONE}` +
                notDeletedQueryString
            )

        case TablesEnum.CARDS:
            return ""

        case TablesEnum.TRANSACTIONS_INFLOW:
            return (
                `&transactionData.amount[gte]=0&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}` +
                transactionArchiveAfterXDaysQueryString
            )
        case TablesEnum.TRANSACTIONS_OUTFLOW:
            return (
                `&transactionData.amount[lt]=0&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}` +
                transactionArchiveAfterXDaysQueryString
            )
        case TablesEnum.EXPENSE_TRANSACTIONS:
            return (
                `&status[ne]=${TransactionStatusEnum.FAILED}&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}` +
                transactionArchiveAfterXDaysQueryString
            )
        case TablesEnum.TRANSACTIONS_ARCHIVE:
            return (
                `&archived[is]=true&transactionType[in]=${TransactionTypeEnum.BANK_TRANSACTION},${TransactionTypeEnum.CSV_TRANSACTION},${TransactionTypeEnum.CARD_TRANSACTION}` +
                transactionArchiveAfterXDaysQueryString
            )
        default:
            return ""
    }
}

/**
 * Returns the filter query string that the user sets using the filter modals/buttons..
 */
export const getTableFilterQueryString = (table: TablesEnum, filterObject: any) => {
    const useGross = store.getState().auth.user?.settings?.showGrossAmount
    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_INVOICE_AND_PURCHASE:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ARCHIVE:
        case TablesEnum.INBOX_INVOICE:
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
        case TablesEnum.INBOX_INVOICE_MODAL_INVOICES:
        case TablesEnum.ALL_REQUESTS:
        case TablesEnum.CARDS:
        case TablesEnum.EMPLOYEES:
        case TablesEnum.SUBSCRIPTIONS:
        case TablesEnum.POSSIBLE_MATCHES:
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.EXPENSE_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
            return getFilterQueryString(filterObject, useGross)

        default:
            return ""
    }
}

/**
 * Returns the sort that the table has when the user hasn't selected any particular sorting.
 */
export const getTableDefaultSort = (table: TablesEnum): { field: string; order: string } => {
    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_INVOICE_AND_PURCHASE:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.ALL_REQUESTS:
            // oldest requests first
            return { field: "datePurchased", order: "ascend" }
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.IN_PROGRESS:
            return { field: "datePurchased", order: "descend" }
        case TablesEnum.CARDS:
            return { field: "userObject.firstName", order: "descend" }
        case TablesEnum.EMPLOYEES:
        case TablesEnum.EMPLOYEE_VENDORS:
            return { field: "firstName", order: "descend" }
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
            return { field: "transactionData.date", order: "descend" }
        case TablesEnum.EXPENSE_TRANSACTIONS:
            return { field: "probabilityForExpense", order: "descend" }
        case TablesEnum.CARD_TRANSACTIONS:
            return { field: "transactionData.date", order: "descend" }
        case TablesEnum.SUBSCRIPTIONS:
            return { field: "name", order: "ascend" }

        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
            return { field: "probability", order: "descend" }
        default:
            return { field: "", order: "" }
    }
}

/**
 * Returns the search query string for a table that the user sets when using search boxes.
 */
export const getTableSearchQueryString = (table: TablesEnum, search: string): string => {
    let searchTargets: string[] = []
    const useGross = store.getState().auth.user?.settings?.showGrossAmount

    switch (table) {
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_INVOICE_AND_PURCHASE:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.ALL_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ARCHIVE:
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
            searchTargets = [
                "approverName",
                "requesterName",
                "vendorName",
                "invoiceNumber",
                "description",
                "items.name",
                "expenseNumber",
                useGross ? "totalGrossPrice" : "totalNetPrice",
            ]
            break
        case TablesEnum.SUBSCRIPTIONS:
            searchTargets = ["invoiceNumber", "description", "name", "items.name", useGross ? "totalGrossPrice" : "totalNetPrice"]
            break
        case TablesEnum.CARDS:
            searchTargets = ["cardName", "last4Digits", "userObject.firstName", "userObject.lastName"]
            break
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.EXPENSE_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
            searchTargets = ["transactionData.description", "transactionData.iban", "transactionData.amount", "vendor"]
            break
        case TablesEnum.EMPLOYEES:
        case TablesEnum.EMPLOYEE_VENDORS:
            searchTargets = ["firstName", "lastName"]
            break
        case TablesEnum.INBOX_INVOICE:
        case TablesEnum.INBOX_INVOICE_MODAL_INVOICES:
            searchTargets = ["fileName", "uploadedByObject.firstName", "uploadedByObject.lastName", "vendorName"]
            break
        default:
            searchTargets = []
    }

    return convertSearchValueToQuery(search, searchTargets)
}

export const getTableDocType = (table: TablesEnum) => {
    switch (table) {
        case TablesEnum.INBOX_INVOICE:
        case TablesEnum.INBOX_INVOICE_MODAL_INVOICES:
            return TableDocType.INBOX_INVOICE
        case TablesEnum.TODO_PURCHASE_APPROVAL:
        case TablesEnum.TODO_INVOICE_AND_PURCHASE:
        case TablesEnum.TODO_INVOICE_APPROVAL:
        case TablesEnum.TODO_DOCS_NEEDED:
        case TablesEnum.TODO_APPROVAL_PENDING:
        case TablesEnum.REVIEW_REQUESTS:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_PAID:
        case TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED:
        case TablesEnum.DONE_REQUESTS:
        case TablesEnum.IN_PROGRESS:
        case TablesEnum.ALL_REQUESTS:
        case TablesEnum.SUBSCRIPTIONS:
        case TablesEnum.ARCHIVE:
        case TablesEnum.INBOX_INVOICE_MODAL_EXPENSES:
            return TableDocType.EXPENSE
        case TablesEnum.CARDS:
            return TableDocType.CARD
        case TablesEnum.CARD_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_INFLOW:
        case TablesEnum.TRANSACTIONS_OUTFLOW:
        case TablesEnum.EXPENSE_TRANSACTIONS:
        case TablesEnum.TRANSACTIONS_ARCHIVE:
            return TableDocType.TRANSACTION
        case TablesEnum.EMPLOYEES:
        case TablesEnum.EMPLOYEE_VENDORS:
            return TableDocType.EMPLOYEE
        case TablesEnum.COST_CENTERS:
            return TableDocType.COST_CENTER
        case TablesEnum.VENDORS:
            return TableDocType.VENDOR
        case TablesEnum.WORKFLOWS:
            return TableDocType.WORKFLOW
        case TablesEnum.TRANSACTION_CATEGORIES_INFLOW:
        case TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW:
            return TableDocType.TRANSACTION_CATEGORY
        default:
            return ""
    }
}

let tablesEnumMapObject: any
/**
 * Concatenates the string passed in the first parameter with the enum key of the table passed in the second parameter.
 * The return value should match the enums declared here: {@link TableActionsTypes}.
 *
 * Example
 *  ```js
 * const tableType = getTableType("UPDATE_TABLE_PAGINATION", TablesEnum.EMPLOYEES)
 * console.log(tableType) // "UPDATE_TABLE_PAGINATION_EMPLOYEES"
 * ```
 */
export const getTableType = (tableType: string, table: TablesEnum) => {
    if (!tablesEnumMapObject) {
        tablesEnumMapObject = {}
        for (const [key, value] of Object.entries(TablesEnum)) {
            tablesEnumMapObject[value] = key
        }
    }
    const enumTable = tablesEnumMapObject[table]
    return `${tableType}_${enumTable}`
}

export const getFilterTableType = (tableType: UpdateTableFilterType, table: TablesEnum) => {
    // If table is from global dashboard filter return
    if (table === TablesEnum.TODO_INVOICE_APPROVAL) {
        return TableActionsTypes.UPDATE_DASHBOARD_TABLES_FILTER
    }

    return getTableType(tableType, table)
}

export const isTableFilterApplied = (filterObject: { [key: string]: string | object | string[] }): boolean => (Object.keys(filterObject).length ? true : false)
